/** @format */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiMail } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { AuthButton } from '../common/AuthButton';
import TextInput from './TextInput';
import PasswordInput from './PasswordInput';
import { useAuthStore } from '../../context/useAuthStore'; // Zustand store hook
import { Link } from 'react-router-dom';
import { ROUTES } from '../../constant/routes';
import { validateEmail } from '../../utils/validation';
import AuthLink from '../common/AuthLink';

interface LoginFormProps {
	onSuccess?: (roleId: string) => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSuccess }) => {
	const { login, user } = useAuthStore();
	const [form, setForm] = useState({ emailId: '', password: '' });
	const [errors, setErrors] = useState({ emailId: '', password: '' });
	const [loading, setLoading] = useState(false);
	const [ipAddress, setIpAddress] = useState('');

	useEffect(() => {
		const fetchIp = async () => {
			try {
				const res = await axios.get('https://ipapi.co/json/');
				setIpAddress(res.data?.ip ?? '');
			} catch {
				// ignore
			}
		};
		fetchIp();
	}, []);

	useEffect(() => {
		if (user?.roleId) {
			onSuccess?.(user.roleId);
		}
	}, [user, onSuccess]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setForm((prev) => ({ ...prev, [name]: value }));

		if (errors[name as keyof typeof errors]) {
			setErrors((prev) => ({ ...prev, [name]: '' }));
		}
	};

	const validateField = (fieldName: keyof typeof form, value: string) => {
		if (!value.trim()) return 'This field is required';
		if (fieldName === 'emailId' && !validateEmail(value)) {
			return 'Please enter a valid email address';
		}
		if (fieldName === 'password' && value.length < 8) {
			return 'Password must be at least 8 characters';
		}
		return '';
	};

	const validateForm = () => {
		let isValid = true;
		const newErrors = { ...errors };

		(Object.keys(form) as (keyof typeof form)[]).forEach((key) => {
			const error = validateField(key, form[key]);
			newErrors[key] = error;
			if (error) isValid = false;
		});

		setErrors(newErrors);
		return isValid;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validateForm()) {
			toast.error('Please fix the errors in the form');
			return;
		}

		setLoading(true);

		try {
			const loggedInUser = await login({
				emailId: form.emailId.trim().toLowerCase(),
				password: form.password,
				ipAddress,
			});

			if (loggedInUser) {
				setForm({ emailId: '', password: '' });
				toast.success('Login Successful!');
			}
		} catch (error: unknown) {
			let errorMessage = 'Login failed. Please try again.';
			if (error instanceof Error) {
				errorMessage = error.message;
			}
			toast.error(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	const isFormValid =
		Object.values(form).every((field) => field.trim().length > 0) &&
		Object.values(errors).every((error) => !error);

	return (
		<div className='flex flex-col gap-4 w-full justify-between'>
			<form
				onSubmit={handleSubmit}
				className='space-y-4'>
				<TextInput
					id='emailId'
					name='emailId'
					label='Email Address'
					type='email'
					value={form.emailId}
					onChange={handleChange}
					placeholder='Enter your email address'
					required
					errorMessage={errors.emailId}
					icon={FiMail}
				/>
				<PasswordInput
					id='password'
					name='password'
					label='Password'
					value={form.password}
					onChange={handleChange}
					placeholder='Enter your password'
					required
					errorMessage={errors.password}
					disabled={loading}
				/>
				<div className='text-right -mt-4 mr-4'>
					<Link
						to={ROUTES.FORGOT_PASSWORD}
						className='forgotPasswordLink'>
						Forgot Password?
					</Link>
				</div>
				<AuthButton
					title='Sign In'
					loadingTitle='Logging In...'
					loading={loading}
					disabled={!isFormValid || loading}
				/>
			</form>
			<AuthLink
				text="Don't have an account?"
				linkText='Register.'
				linkTo={ROUTES.REGISTER}
			/>
		</div>
	);
};
